import React, { useEffect, useRef, useState } from "react"
import styles from "./dashboard.module.css"
import Card from "./Card"
import Select from "../../components/Form/Select"
import { BiFilter } from "react-icons/bi"
import { areObjectsEqual, getUsersByTeams, getValidUserIdsByTeams } from "../../utils/utils"
import { useAuth } from "../../contexts/authContext"
import api from "../../services/api"
import { toast } from "react-toastify"
import SideFilter from './SideFilter/index'
import FormGroupInline from "../../components/Form/FormGroupInline"
import Button from "../../components/Form/Button"
import DatePicker from "../../components/Form/DatePicker"
import Loading from '../layouts/Loading/index'
import { useData } from "../../contexts/dataContext"
import { getStages } from "../../schemas/Stage"
import moment from 'moment'
import imageFilterDash from "../../images/filter-dash.png"
import BackgroundSelectFilter from "../../components/BackgroundSelectFilter"
import { NavLink, useParams } from "react-router-dom"
import Chart from "./Charts/Chart"
import { resolveRelativeLeadFilterDates, serializeLeadFilters } from "../../utils/leadFilters"
import PdfExportButton from "./PdfExportButton"
import { formatPeriod, getOptionLabels } from "../../utils/dashboardPdf"
import { getSavedLeadFilters } from "../../schemas/SavedLeadFilter"
import ActiveFilterChips, { buildLeadFilterChips } from "../../components/LeadFilters/ActiveFilterChips"

const collectionOperatorLabels = {
    any: "Qualquer um dos selecionados",
    all: "Todos os selecionados",
    exact: "Exatamente os selecionados",
    none: "Nenhum dos selecionados",
    is_empty: "Nenhum cadastrado",
    is_not_empty: "Pelo menos um cadastrado",
}

const additionalOperatorLabels = {
    equals: "É igual a",
    contains: "Contém",
    starts_with: "Começa com",
    before: "Até a data",
    after: "A partir da data",
    between: "Entre",
    gt: "Maior que",
    gte: "Maior ou igual a",
    lt: "Menor que",
    lte: "Menor ou igual a",
    any: "Possui qualquer uma",
    all: "Possui todas",
    exact: "Possui exatamente",
    none: "Não possui nenhuma",
    is_set: "Está preenchido",
    is_not_set: "Não está preenchido",
}

const utmFieldLabels = {
    source: "UTM Source",
    medium: "UTM Medium",
    campaign: "UTM Campaign",
    content: "UTM Content",
    term: "UTM Term",
    url: "UTM URL",
}

const initialFilters = { dateStart: moment().subtract(7, 'd').format("YYYY-MM-DD"), dateEnd: moment().subtract(1, 'd').format("YYYY-MM-DD"), datePreset: "last7Days", type: "date" }

const DashboardKanban = () => {
    const {
        funnels, teams, users, adAccounts, loadCampaignsByOrigins, products, origins,
        campaigns, flagsByoptions, additionalFields, metaAdsAdsName, metaAdsAdsetsName,
        metaAdsCampaignsName, metaAdsFormsName, metaAdsPagesName,
    } = useData()
    const [data, setData] = useState({})
    const [stages, setStages] = useState([])
    const [filters, setFilters] = useState(initialFilters)
    const [filtersTemp, setFiltersTemp] = useState(initialFilters)
    const [changedFilters, setChangedFilters] = useState(false)
    const [sideFilterActive, setSideFilterActive] = useState(true)
    const [loading, setLoading] = useState(false)
    const [backgroundFilter, setBackgroundFilter] = useState(true)
    const defaultFilterLoaded = useRef("")
    const dashboardRequestId = useRef(0)

    const { permissions, account, user } = useAuth()
    const { account_id } = useParams()

    let isLoadingDashboard = false

    const getAvailableUsers = selectedTeams => selectedTeams?.length
        ? getUsersByTeams(selectedTeams, users, teams)
        : users || []

    const sanitizeUsersByTeams = (filterValues, previousTeams) => {
        return {
            ...filterValues,
            users: getValidUserIdsByTeams(filterValues?.users, filterValues?.teams, users, teams, [], previousTeams),
        }
    }

    const availableUsers = getAvailableUsers(filtersTemp?.teams)

    useEffect(() => {
        setInitialValuesByFilter("funnels", funnels?.map(funnel => { funnel.selected = true; return funnel }))
    }, [funnels])

    useEffect(() => {
        setInitialValuesByFilter("teams", teams?.map(team => { team.selected = true; return team }))
    }, [teams])

    useEffect(() => {
        const selectedTeamIds = teams?.map(team => team.id) || []
        setInitialValuesByFilter("users", getAvailableUsers(selectedTeamIds).map(user => ({ ...user, selected: true })))
    }, [users, teams])

    useEffect(() => {
        const defaultFilterKey = `${account_id || ""}:${user?._id || ""}`
        if (defaultFilterLoaded.current === defaultFilterKey || !account_id || !user?._id || !funnels?.length || !teams?.length || !users?.length) return
        defaultFilterLoaded.current = defaultFilterKey

        getSavedLeadFilters("dashboard-kanban").then(savedFilters => {
            const defaultFilter = savedFilters.find(item => item.is_default)
            if (defaultFilter?.filters) {
                const resolvedFilters = sanitizeUsersByTeams(resolveRelativeLeadFilterDates(defaultFilter.filters))
                setFiltersTemp(resolvedFilters)
                getDataByFilter(true, resolvedFilters)
            }
        }).catch(() => { })
    }, [account_id, user?._id, funnels, teams, users])

    useEffect(() => {
        handleFiltersChanged()
    }, [filtersTemp])

    useEffect(() => {
        loadCampaignsByOrigins(filtersTemp.origins)
    }, [filtersTemp.origins])

    useEffect(() => {
        setStages([])
        if (filtersTemp?.funnels?.length > 0) {
            updateStagesByFunnelId(filtersTemp.funnels)
        }
    }, [filtersTemp.funnels])

    const getDataByFilter = async (updateFilters = false, filterValues = filtersTemp) => {
        const requestId = ++dashboardRequestId.current
        const resolvedFilterValues = resolveRelativeLeadFilterDates(filterValues)
        try {
            if (!isLoadingDashboard) {
                isLoadingDashboard = true
                setLoading(true)
                const queryString = serializeLeadFilters(resolvedFilterValues)
                const response = await api.get(`/leads/dashboard?${queryString}`)
                if (requestId !== dashboardRequestId.current) return
                const responseData = response.data?.data
                setData(responseData)
                if (updateFilters) {
                    setFilters(resolvedFilterValues)
                    setFiltersTemp(resolvedFilterValues)
                }
                setChangedFilters(false)
                setBackgroundFilter(false)
                closeSideFilter()
            }
        } catch (err) {
            if (requestId === dashboardRequestId.current) toast.error(err?.response?.data?.message)
        } finally {
            if (requestId === dashboardRequestId.current) {
                setLoading(false)
                isLoadingDashboard = false
            }
        }
    }

    const updateStagesByFunnelId = (funnels) => {
        funnels?.map(async funnel_id => {
            const listStages = await getStages(funnel_id)
            const newStages = listStages?.map(stage => { return { id: stage._id, value: stage.name } })
            setStages((previousValue) => {
                const previousIds = new Set(previousValue?.map(stage => stage.id))
                const uniqueNewStages = newStages?.filter(newStage => !previousIds.has(newStage.id)) || []
                return [...previousValue, ...uniqueNewStages]
            })
        })
    }

    const setInitialValuesByFilter = (name, array) => {
        if (array?.length > 0) {
            const newValue = array?.filter(el => el.selected)?.map(el => { return el.id })
            setFilters(previousValue => {
                return {
                    ...previousValue,
                    [name]: newValue
                }
            })
            setFiltersTemp(previousValue => {
                return {
                    ...previousValue,
                    [name]: newValue
                }
            })
        }
    }

    const handleChangeFilters = (name, value) => {
        setFiltersTemp(previousValue => {
            const operatorKey = ["users", "products", "flags"].includes(name) ? `${name}Operator` : undefined
            const resetPresenceOperator = operatorKey && value?.length > 0 && ["is_empty", "is_not_empty"].includes(previousValue[operatorKey])
            const nextFilters = { ...previousValue, [name]: value, ...(resetPresenceOperator ? { [operatorKey]: "any" } : {}) }
            return name === "teams" ? sanitizeUsersByTeams(nextFilters, previousValue.teams) : nextFilters
        })
    }

    const handleFiltersChanged = () => {
        setChangedFilters(!areObjectsEqual(filters, filtersTemp))
    }

    const handleChangePeriod = (dateStart, dateEnd, datePreset = null) => {
        handleChangeFilters("dateStart", dateStart)
        handleChangeFilters("dateEnd", dateEnd)
        handleChangeFilters("datePreset", datePreset)
    }

    const cleanFilters = () => {
        setFiltersTemp(initialFilters)
    }

    const openSideFilter = () => {
        setSideFilterActive(true)
    }

    const closeSideFilter = () => {
        setSideFilterActive(false)
    }

    const formatCollectionFilter = (values, operator = "any", options = []) => {
        const operatorLabel = collectionOperatorLabels[operator] || operator
        if (["is_empty", "is_not_empty"].includes(operator)) return operatorLabel
        return `${operatorLabel}: ${getOptionLabels(values, options, "Nenhum selecionado")}`
    }

    const formatAdditionalFilters = () => {
        if (!filters.additionalFieldFilters?.length) return "Nenhum"
        return filters.additionalFieldFilters.map(filter => {
            const field = additionalFields?.find(item => String(item._id) === String(filter.field_id))
            const operator = additionalOperatorLabels[filter.operator] || filter.operator
            const rawValue = Array.isArray(filter.value) ? filter.value.join(", ") : filter.value
            const value = ["is_set", "is_not_set"].includes(filter.operator)
                ? ""
                : ` ${rawValue || "-"}${filter.value_to ? ` e ${filter.value_to}` : ""}`
            return `${field?.name || "Campo"} — ${operator}${value}`
        }).join("; ")
    }

    const formatUtmFilters = () => {
        if (!filters.utmFilters?.length) return "Nenhum"
        return filters.utmFilters.map(filter => {
            const operator = additionalOperatorLabels[filter.operator] || filter.operator
            const value = ["is_set", "is_not_set"].includes(filter.operator) ? "" : ` ${filter.value || "-"}`
            return `${utmFieldLabels[filter.field] || "UTM"} — ${operator}${value}`
        }).join("; ")
    }

    const reportFilters = [
        { label: "Período", value: formatPeriod(filters.dateStart, filters.dateEnd) },
        { label: "Funis", value: getOptionLabels(filters.funnels, funnels) },
        { label: "Etapas", value: getOptionLabels(filters.stages, stages) },
        { label: "Equipes", value: getOptionLabels(filters.teams, teams) },
        ...(permissions?.crm > 1 ? [{ label: "Responsáveis", value: formatCollectionFilter(filters.users, filters.usersOperator, users) }] : []),
        { label: "Produtos", value: formatCollectionFilter(filters.products, filters.productsOperator, products) },
        { label: "Origens", value: getOptionLabels(filters.origins, origins) },
        { label: "Campanhas", value: getOptionLabels(filters.campaigns, campaigns) },
        { label: "Etiquetas", value: formatCollectionFilter(filters.flags, filters.flagsOperator, flagsByoptions) },
        { label: "Campos adicionais", value: formatAdditionalFilters() },
        { label: "Aquisição / UTM", value: formatUtmFilters() },
        ...(metaAdsAdsName?.length ? [{ label: "Meta Ads — anúncios", value: getOptionLabels(filters.metaAdsAdsName, metaAdsAdsName) }] : []),
        ...(metaAdsAdsetsName?.length ? [{ label: "Meta Ads — conjuntos", value: getOptionLabels(filters.metaAdsAdsetsName, metaAdsAdsetsName) }] : []),
        ...(metaAdsCampaignsName?.length ? [{ label: "Meta Ads — campanhas", value: getOptionLabels(filters.metaAdsCampaignsName, metaAdsCampaignsName) }] : []),
        ...(metaAdsFormsName?.length ? [{ label: "Meta Ads — formulários", value: getOptionLabels(filters.metaAdsFormsName, metaAdsFormsName) }] : []),
        ...(metaAdsPagesName?.length ? [{ label: "Meta Ads — páginas", value: getOptionLabels(filters.metaAdsPagesName, metaAdsPagesName) }] : []),
    ]

    const activeFilterChips = buildLeadFilterChips({
        filters,
        dashboard: true,
        options: {
            funnels, stages, teams, users, products, origins, campaigns, flags: flagsByoptions,
            metaAdsAdsName, metaAdsAdsetsName, metaAdsCampaignsName, metaAdsFormsName, metaAdsPagesName,
        },
    })

    return (
        <div className={styles.container}>
            <div className={styles.filters}>
                <div className={styles.groupLeft}>
                    <Select name="Funil" placeholder="Pesquise um funil" options={funnels || []} selected={filtersTemp.funnels || []} onChange={(value) => handleChangeFilters("funnels", value)} multiple={true} />
                    <DatePicker name="Período" dateStart={filtersTemp.dateStart || ""} dateEnd={filtersTemp.dateEnd || ""} onChange={(dateStart, dateEnd, _timeStart, _timeEnd, metadata) => handleChangePeriod(dateStart, dateEnd, metadata?.preset)} multiple={true} />
                    <Select name="Equipe" placeholder="Pesquise uma equipe" options={teams || []} selected={filtersTemp.teams || []} onChange={(value) => handleChangeFilters("teams", value)} multiple={true} />
                    {permissions?.crm > 1 &&
                        <Select name="Responsável" placeholder="Pesquise um responsável" options={availableUsers} selected={filtersTemp.users || []} onChange={(value) => handleChangeFilters("users", value)} multiple={true} />
                    }
                </div>
                <div className={styles.groupRight}>
                    <PdfExportButton
                        title="Dashboard Kanban"
                        accountName={account?.company?.fantasy_name || account?.name}
                        generatedBy={user?.name}
                        filters={reportFilters}
                        data={data}
                        disabled={changedFilters || backgroundFilter || loading}
                    />
                    <div className={styles.rightFilterAction} onClick={openSideFilter}>
                        <BiFilter />
                    </div>
                </div>
            </div>
            {!backgroundFilter && <ActiveFilterChips items={activeFilterChips} onOpen={openSideFilter} />}
            {adAccounts?.length > 0 &&
                <div className={styles.nav}>
                    <div className={styles.link}>
                        <NavLink to={`/${account_id}/dashboard-meta`}>Dashboard Meta Ads</NavLink>
                    </div>
                </div>
            }
            <div className={styles.body}>
                {(changedFilters || backgroundFilter) &&
                    <div className={styles.changedFilters}>
                        <FormGroupInline>
                            <Button variant='secondary' type="submit" onClick={() => getDataByFilter(true)}>Aplicar Filtros</Button>
                            <Button variant='primary' type="submit" onClick={() => setFiltersTemp(filters)}>Limpar</Button>
                        </FormGroupInline>
                    </div>
                }
                {!backgroundFilter &&
                    <>
                        <div className={styles.cards}>
                            {data?.cards?.length > 0 && data.cards.map(item => (
                                <Card
                                    key={item.id}
                                    name={item.name}
                                    value={item.value}
                                    variation={item.variation}
                                    is_positive={item.is_positive}
                                    description={item.description}
                                    tooltip={item.tooltip || item.description}
                                />
                            ))}
                        </div>
                        <div className={styles.charts}>
                            {data?.charts?.length > 0 && data.charts.map(item => (
                                <Chart
                                    key={item.id}
                                    data={item}
                                />
                            ))}
                        </div>
                    </>
                }
                {/* <div className={styles.tables}>
                    {data?.tables?.length > 0 && data.tables.map(item => (
                        <div className={styles.groupTable} key={item.id}>
                            <BreakForm title={item.title} showAction={false} border={false} />
                            <Table
                                className={styles.table}
                                headers={item.headers}
                                values={item.values}
                                messageNotRegisters="Nenhum dado para exibir"
                                showIndex={true}
                            />
                        </div>
                    ))}
                </div> */}
                {backgroundFilter &&
                    <BackgroundSelectFilter
                        text="Selecione os filtros desejados e aplique para visualizar os dados!"
                        image={imageFilterDash}
                    />
                }
            </div>
            {sideFilterActive &&
                <SideFilter
                    filters={filtersTemp}
                    stages={stages}
                    handleChange={handleChangeFilters}
                    onLoadFilters={filterValues => setFiltersTemp(sanitizeUsersByTeams(filterValues))}
                    cleanFilters={cleanFilters}
                    onSubmit={() => getDataByFilter(true)}
                    onClose={closeSideFilter}
                />
            }
            {loading && <Loading fullPage={true} />}
        </div>
    )
}

export default DashboardKanban
